/**
 * V2FirstRunTour.jsx
 * ─────────────────────────────────────────────────────────────────────────
 * Guided tour primitive used by:
 *   • V2FirstRunTour        — welcome tour on Kindra OS home (mounted in
 *                             V2AdminOSPage). localStorage:
 *                               kindra-tour-dismissed / kindra-tour-step
 *   • V2BlueprintTour       — 3-step Blueprint-section tour (mounted in
 *                             V2AdminBlueprintPage). Isolated storage:
 *                               kindra-bp-tour-dismissed / kindra-bp-tour-step
 *
 * ISOLATION RULES
 *   1. Each tour uses its own localStorage keys so dismissing one never
 *      hides the other.
 *   2. The Blueprint tour refuses to render until the OS welcome tour has
 *      been dismissed — the two never stack.
 *   3. Only one tour is mounted per route (OS tour on /admin/os, Blueprint
 *      tour on /admin/os/blueprint*), so they can never overlap visually.
 *
 * Anchors are resolved via `data-testid` on real DOM. If an anchor is not
 * present on the current route the step soft-fails to a centred callout
 * with a "Take me there" button (per-tour navigation handler).
 *
 * Reset entry points:
 *   • tourReset()           — replay the OS welcome tour
 *   • blueprintTourReset()  — replay the Blueprint tour
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { V2OSC as C } from './V2AdminOSPage';

// ─── OS welcome-tour config ──────────────────────────────────────────────
const OS_TOUR_KEY = 'kindra-tour-dismissed';
const OS_TOUR_STEP_KEY = 'kindra-tour-step';

const OS_STEPS = [
  {
    id: 'daily-list',
    anchorTestIds: ['os-home-explainer'],
    title: "Here's your daily list",
    body: "Every finished client chat lands here. Filter to \"Ready to review\" to see what needs your eyes today. Nothing is sent to a client just by opening the queue.",
    placement: 'below',
  },
  {
    id: 'open-a-report',
    anchorTestIds: ['os-first-row'],
    title: 'Click a row to read the report',
    body: "Kindra's already written a personalised report from the client's chat. Opening it is safe — you'll only see. Nothing sends. If it looks wrong, there's an edit button on the next page.",
    placement: 'below',
  },
  {
    id: 'nothing-sends',
    anchorTestIds: ['os-send-client-report'],
    title: 'Nothing sends without a confirmation',
    body: "Every button that emails a real client opens a check-first modal. Preview freely — real sends only happen after your explicit confirmation click. When in doubt, cancel.",
    placement: 'above',
  },
];

// ─── Blueprint-tour config ───────────────────────────────────────────────
const BP_TOUR_KEY = 'kindra-bp-tour-dismissed';
const BP_TOUR_STEP_KEY = 'kindra-bp-tour-step';

const BP_STEPS = [
  {
    id: 'bp-onebox',
    anchorTestIds: ['blueprint-onebox'],
    title: 'Ask it like you would a mate',
    body: "One plain-English question is all you type. Six experts for quick plans — or convene the full thirteen-seat boardroom for a whole-business deep dive. Under the OneBox you can also paste a few paragraphs about the business so the experts have real context to work from.",
    placement: 'above',
  },
  {
    id: 'bp-working',
    // Prefer the live "sitting" screen; soft-fail to the most recent
    // library card so returning users still get an anchored step.
    anchorTestIds: ['blueprint-sitting', 'bp-first-library-card'],
    title: 'Six experts, one independent reviewer',
    body: "While you wait, six named experts argue the answer out and an independent reviewer checks their work. It usually takes 2–5 minutes. You can safely leave this screen — nothing sends to a client, and the blueprint keeps working in the background.",
    placement: 'below',
  },
  {
    id: 'bp-actions',
    anchorTestIds: ['bp-actions-row'],
    title: 'Grow, argue, ask — nothing sends',
    body: "Each stage has a Grow button for deeper detail. You can read how the experts argued (with the turns that changed the plan flagged), or ask a follow-up question. Nothing on this row emails or sends anything to a client — it's all safe to click.",
    placement: 'above',
  },
];

// ─── Reset helpers (called from the Help page) ───────────────────────────
export function tourReset() {
  try {
    localStorage.removeItem(OS_TOUR_KEY);
    localStorage.removeItem(OS_TOUR_STEP_KEY);
  } catch (_e) { /* noop */ }
  window.location.assign('/admin/os');
}

export function blueprintTourReset() {
  try {
    localStorage.removeItem(BP_TOUR_KEY);
    localStorage.removeItem(BP_TOUR_STEP_KEY);
  } catch (_e) { /* noop */ }
  window.location.assign('/admin/os/blueprint');
}

// ─── Shared tour primitive ───────────────────────────────────────────────
// Renders a dim veil + highlight ring + callout, pinned to the first
// resolved `data-testid` anchor. If none present, falls back to a centred
// callout with a "Take me there" button (handler provided per-tour).
function GuidedTour({
  tourId,
  steps,
  storageKey,
  stepKey,
  eyebrow,
  finishLabel,
  onTakeMeThere,
  gate,
}) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch (_e) { return true; }
  });
  const [step, setStepRaw] = useState(() => {
    try {
      const n = parseInt(localStorage.getItem(stepKey) || '0', 10);
      return Number.isFinite(n) && n >= 0 && n < steps.length ? n : 0;
    } catch (_e) { return 0; }
  });
  const setStep = (n) => {
    setStepRaw(n);
    try { localStorage.setItem(stepKey, String(n)); } catch (_e) { /* noop */ }
  };
  const [anchor, setAnchor] = useState(null);
  const tickRef = useRef(null);

  // Anchor tracking — polls for the first-resolvable anchor on every step
  // + on scroll so the callout stays pinned even as layouts virtualise.
  useEffect(() => {
    if (dismissed) return undefined;
    if (gate && !gate()) return undefined;
    let scrolled = false;
    const find = () => {
      const t = steps[step];
      if (!t) return null;
      let el = null;
      for (const testid of t.anchorTestIds) {
        el = document.querySelector(`[data-testid~="${testid}"]`);
        if (el) break;
      }
      if (el) {
        if (!scrolled) {
          try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
          catch (_e) { /* noop */ }
          scrolled = true;
        }
        const r = el.getBoundingClientRect();
        setAnchor({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setAnchor(null);
      }
      return el;
    };
    find();
    tickRef.current = setInterval(find, 400);
    window.addEventListener('scroll', find, { passive: true });
    window.addEventListener('resize', find);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      window.removeEventListener('scroll', find);
      window.removeEventListener('resize', find);
    };
  }, [step, dismissed, steps, gate]);

  // Non-blocking dismissal: any pointerdown outside the callout skips the
  // tour WITHOUT swallowing the click — nav/tab clicks go through first try.
  useEffect(() => {
    if (dismissed) return undefined;
    if (gate && !gate()) return undefined;
    const onDown = (e) => {
      if (e.target && e.target.closest && e.target.closest('[data-tour-callout="1"]')) return;
      try {
        localStorage.setItem(storageKey, '1');
        localStorage.removeItem(stepKey);
      } catch (_e) { /* noop */ }
      setDismissed(true);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [dismissed, gate, storageKey, stepKey]);

  if (dismissed) return null;
  if (gate && !gate()) return null;

  const cur = steps[step];
  const isLast = step === steps.length - 1;

  const skip = () => {
    try {
      localStorage.setItem(storageKey, '1');
      localStorage.removeItem(stepKey);
    } catch (_e) { /* noop */ }
    setDismissed(true);
  };
  const next = () => {
    if (isLast) return skip();
    setStep(step + 1);
  };
  const takeMeThere = () => {
    if (onTakeMeThere) onTakeMeThere({ step, cur });
  };

  // Callout positioning — clamped to viewport so it never overflows mobile.
  const VW = window.innerWidth;
  const VH = window.innerHeight;
  const CALLOUT_W = Math.min(VW - 32, 360);
  const CALLOUT_H_EST = 240;
  let calloutTop = 100;
  let calloutLeft = 16;
  if (anchor) {
    if (cur.placement === 'above') {
      calloutTop = Math.max(16, anchor.top - CALLOUT_H_EST - 12);
    } else {
      calloutTop = anchor.top + anchor.height + 12;
    }
    calloutTop = Math.min(calloutTop, VH - CALLOUT_H_EST - 16);
    calloutTop = Math.max(calloutTop, 16);
    calloutLeft = Math.min(VW - CALLOUT_W - 16, Math.max(16, anchor.left));
  } else {
    calloutTop = 120;
    calloutLeft = Math.max(16, (VW - CALLOUT_W) / 2);
  }

  return (
    <div data-testid={`${tourId}-tour-root`} style={{
      position: 'fixed', inset: 0, zIndex: 90, pointerEvents: 'none',
    }}>
      <div
        data-testid={`${tourId}-tour-veil`}
        style={{ position: 'absolute', inset: 0,
                  background: 'rgba(16,8,56,0.55)',
                  backdropFilter: 'blur(2px)',
                  pointerEvents: 'none' }} />

      {anchor && (
        <div style={{
          position: 'fixed',
          top: anchor.top - 6, left: anchor.left - 6,
          width: anchor.width + 12, height: anchor.height + 12,
          border: `2px solid ${C.accent}`,
          borderRadius: 12,
          boxShadow: `0 0 0 4px ${C.accent}22, 0 0 40px ${C.accent}44`,
          pointerEvents: 'none',
        }} />
      )}

      <div
        data-testid={`${tourId}-tour-step-${cur.id}`}
        data-tour-callout="1"
        style={{
          position: 'fixed',
          top: calloutTop, left: calloutLeft, width: CALLOUT_W,
          background: '#1B144B',
          border: `1px solid ${C.accent}66`,
          borderRadius: 14,
          padding: '18px 20px',
          color: C.icy,
          fontFamily: 'Inter',
          pointerEvents: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          wordBreak: 'break-word', overflowWrap: 'anywhere',
        }}>
        <div style={{
          fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: C.accent, fontWeight: 700, marginBottom: 6,
        }}>
          {eyebrow} · Step {step + 1} of {steps.length}
        </div>
        <h3 style={{ fontFamily: 'Poppins', fontSize: 17, margin: '4px 0 8px', fontWeight: 600, color: C.icy, letterSpacing: -0.2 }}>
          {cur.title}
        </h3>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'rgba(220,240,245,0.85)' }}>
          {cur.body}
        </p>

        {!anchor && onTakeMeThere && (
          <button
            data-testid={`${tourId}-tour-take-me-there`}
            onClick={takeMeThere}
            style={{
              marginTop: 12, padding: '7px 14px', borderRadius: 999,
              background: 'transparent', border: `1px solid ${C.hair}`,
              color: C.muted, cursor: 'pointer', fontSize: 12,
            }}>
            Take me to this step →
          </button>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <button
            data-testid={`${tourId}-tour-skip`}
            onClick={skip}
            style={{
              padding: '7px 12px', borderRadius: 999,
              background: 'transparent', border: 'none',
              color: C.faint, cursor: 'pointer', fontSize: 12,
            }}>
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {steps.map((_, i) => (
              <span key={i} style={{
                width: 6, height: 6, borderRadius: 999,
                background: i === step ? C.accent : 'rgba(220,240,245,0.2)',
              }} />
            ))}
            <button
              data-testid={isLast ? `${tourId}-tour-finish` : `${tourId}-tour-next`}
              onClick={next}
              style={{
                marginLeft: 8,
                padding: '8px 18px', borderRadius: 999,
                background: C.spectrum, color: '#fff', fontWeight: 600,
                border: 'none', cursor: 'pointer', fontSize: 12.5,
                letterSpacing: 0.2,
              }}>
              {isLast ? finishLabel : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── OS welcome tour (default export, unchanged behaviour) ───────────────
export default function V2FirstRunTour() {
  const nav = useNavigate();
  const onTakeMeThere = () => {
    const rowEl = document.querySelector('[data-testid="os-first-row"]');
    if (rowEl) rowEl.click();
    else nav('/admin/os');
  };
  return (
    <GuidedTour
      tourId="os"
      steps={OS_STEPS}
      storageKey={OS_TOUR_KEY}
      stepKey={OS_TOUR_STEP_KEY}
      eyebrow="Welcome tour"
      finishLabel="Got it — start using Kindra OS"
      onTakeMeThere={onTakeMeThere}
    />
  );
}

// ─── Blueprint tour ──────────────────────────────────────────────────────
// Only renders once the OS welcome tour has been dismissed — the two never
// stack. Auto-triggers on first visit to any /admin/os/blueprint route.
export function V2BlueprintTour() {
  const nav = useNavigate();
  const onTakeMeThere = ({ step }) => {
    if (step === 0) {
      // Step 1 anchors on the OneBox. If we're on the library view, click
      // the "+ New Blueprint" button to reveal it.
      const newBtn = document.querySelector('[data-testid="blueprint-new-btn"]');
      if (newBtn) newBtn.click();
      else nav('/admin/os/blueprint');
    } else if (step === 1) {
      // Step 2 anchors on the working screen or most recent library card.
      // If we're on OneBox / a stale detail page, ship them to the library.
      nav('/admin/os/blueprint');
    } else if (step === 2) {
      // Step 3 needs an open, completed blueprint. Send them to the library
      // so they can pick one; the anchor will pin when they open it.
      nav('/admin/os/blueprint');
    }
  };
  const gate = () => {
    // Never stack with the OS welcome tour.
    try { return localStorage.getItem(OS_TOUR_KEY) === '1'; }
    catch (_e) { return false; }
  };
  return (
    <GuidedTour
      tourId="bp"
      steps={BP_STEPS}
      storageKey={BP_TOUR_KEY}
      stepKey={BP_TOUR_STEP_KEY}
      eyebrow="Blueprint tour"
      finishLabel="Got it — start using Blueprint"
      onTakeMeThere={onTakeMeThere}
      gate={gate}
    />
  );
}
